import { useTranslations } from '@vocab/react';
import {
  Button,
  Dialog,
  Inline,
  RadioGroup,
  RadioItem,
  Stack,
  Text,
} from 'braid-design-system';
import {
  type FC,
  type FormEventHandler,
  type MutableRefObject,
  useState,
} from 'react';

import { FreemiumImage } from 'src/App/AppPageBlock/FreemiumImage';
import type { JobPlacementStatus } from 'src/App/AppPageBlock/PlacementStatusLink';
import type { ResponseErrorReason } from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { TrackOnMount } from 'src/libs/Analytics';
import {
  CoffeeAndBookIllustration,
  ConnectionPlugIllustration,
  DataPresentationIllustration,
} from 'src/libs/Illustrations';
import { ImageModal } from 'src/libs/ImageModal';

import { useFeatureFlag } from '../FeatureFlagsContext';

import translations from './.vocab';
import { AdPerformanceDialogContent } from './AdPerformanceDialogContent';
import { Confetti } from './Confetti';
import { ErrorDialogContent } from './ErrorDialogContent';
import { HiringAdviceDialogContent } from './HiringAdviceDialogContent';
import { useUpdateJobPlacementMutation } from './JobPlacementDialog.hirer.generated';
import { UpgradeAdDialogContent } from './UpgradeAdDialogContent';

type JobPlacementFormValue = 'yes' | 'no' | 'not yet' | '';
interface JobPlacementDialogProps {
  jobId: number;
  openDialog: {
    open: boolean;
    isTriggeredFromHeaderLink: boolean;
  };
  onClose: () => void;
  hideJobPlacementInLocalStorage: () => void;
  shouldShowUpgradeAdDialog: boolean;
  shouldShowAdPerformanceDialog: boolean;
  placementV2:
    | {
        __typename?: 'JobPlacementV2ResponseSuccess';
        result: {
          __typename?: 'JobPlacementV2';
          canShowPlacementPrompt: boolean;
          status?: JobPlacementStatus | null;
        };
      }
    | { __typename?: 'ResponseError'; error: ResponseErrorReason };
  onPlacementStatusChange: (status: JobPlacementStatus) => void;
  trackJobPlacementDataSubmissionEvent: (
    optionSelected:
      | 'Yes'
      | 'No'
      | 'Not yet'
      | 'Too early to say'
      | 'Still reviewing applications',
  ) => void;
  trackJobPlacementDialogDismissedEvent: (
    dialogName:
      | 'Candidate Management - Placement - Update Hiring Status'
      | 'Candidate Management - Placement - Hiring Check-In'
      | 'Candidate Management - Placement - Improve Job Ad'
      | 'Candidate Management - Placement - Improve Next Job Ad'
      | "Candidate Management - Placement - Can't Find The Right Candidate"
      | "Candidate Management - Placement - Can't Display Hiring Status"
      | "Candidate Management - Placement - Couldn't Update Hiring Status",
  ) => void;
  trackJobPlacementDialogDisplayedEvent: (
    dialogName:
      | 'Candidate Management - Placement - Update Hiring Status'
      | 'Candidate Management - Placement - Hiring Check-In'
      | 'Candidate Management - Placement - Improve Job Ad'
      | 'Candidate Management - Placement - Improve Next Job Ad'
      | "Candidate Management - Placement - Can't Find The Right Candidate"
      | "Candidate Management - Placement - Can't Display Hiring Status"
      | "Candidate Management - Placement - Couldn't Update Hiring Status",
  ) => void;
  trackJobPlacementViewReportButtonPressedEvent: () => void;
  trackJobPlacementUpgradeButtonPressedEvent: () => void;
  trackJobPlacementViewHiringAdviceButtonPressedEvent: () => void;
  trackJobPlacementHiringAdviceLinkPressedEvent: (
    elementText:
      | 'How to write a great job ad (with examples)'
      | 'Are candidates seeing your job ad? Make these changes'
      | 'Why your job ad may not be standing out'
      | "Losing candidates through the hiring process? Here's what to do"
      | 'Laws Of Attraction: Find out what New Zealand candidates want'
      | 'Make smarter data-driven decisions with Talent Attraction Lab',
  ) => void;
  jobSectionRef: MutableRefObject<HTMLDivElement | null>;
}

export const JobPlacementDialog: FC<JobPlacementDialogProps> = ({
  jobId,
  openDialog,
  onClose,
  hideJobPlacementInLocalStorage,
  trackJobPlacementDataSubmissionEvent,
  trackJobPlacementDialogDismissedEvent,
  trackJobPlacementDialogDisplayedEvent,
  trackJobPlacementViewReportButtonPressedEvent,
  trackJobPlacementUpgradeButtonPressedEvent,
  trackJobPlacementViewHiringAdviceButtonPressedEvent,
  trackJobPlacementHiringAdviceLinkPressedEvent,
  shouldShowUpgradeAdDialog,
  shouldShowAdPerformanceDialog,
  placementV2,
  onPlacementStatusChange,
  jobSectionRef,
}) => {
  const { t } = useTranslations(translations);
  const { router } = useAppConfig();
  const { open, isTriggeredFromHeaderLink } = openDialog;

  const [currentJobPlacement, setCurrentJobPlacement] =
    useState<JobPlacementFormValue>(() => {
      if (placementV2.__typename !== 'JobPlacementV2ResponseSuccess') {
        return '';
      }

      if (placementV2.result.status === 'FILLED') {
        return 'yes';
      }

      if (placementV2.result.status === 'NOT_FILLED') {
        return 'no';
      }

      return '';
    });
  const [shouldShowErrorMessage, setShouldShowErrorMessage] =
    useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<
    | 'JobPlacementDialog'
    | 'UpgradeAdDialog'
    | 'AdPerformanceDialog'
    | 'HiringAdviceDialog'
    | 'UpdateErrorDialog'
  >('JobPlacementDialog');
  const [confettiRunning, setConfettiRunning] = useState(false);
  const [jobSectionRect] = useState<{
    height?: number;
    width?: number;
    bottom?: number;
    top?: number;
    left?: number;
  }>(() => {
    const boundingBox = jobSectionRef.current?.getBoundingClientRect();
    return {
      height: boundingBox?.height,
      width: boundingBox?.width,
      bottom: boundingBox?.bottom,
      top: boundingBox?.top,
      left: boundingBox?.left,
    };
  });

  const [updateJobPlacement, { loading: jobPlacementMutationLoading }] =
    useUpdateJobPlacementMutation({
      onError: () => {
        setShowDialog('UpdateErrorDialog');
      },
    });

  const { variant } = useFeatureFlag('cm_improve_direct_placement_signals');
  const getLabelAndTrackingBasedOnVariant = (): {
    label: string;
    tracking: () => void;
  } => {
    switch (variant) {
      case 'too_early_to_say':
        return {
          label: t('Too early to say'),
          tracking: () =>
            trackJobPlacementDataSubmissionEvent('Too early to say'),
        };
      case 'still_reviewing_applications':
        return {
          label: t('Still reviewing applications'),
          tracking: () =>
            trackJobPlacementDataSubmissionEvent(
              'Still reviewing applications',
            ),
        };
      default:
        return {
          label: t('Not yet'),
          tracking: () => trackJobPlacementDataSubmissionEvent('Not yet'),
        };
    }
  };

  const handleUpdateJobPlacement = (attributes: {
    isPlaced: boolean | undefined;
    submitted: boolean;
    onCompleted: undefined | (() => void);
  }) => {
    updateJobPlacement({
      variables: {
        input: {
          jobId,
          isPlaced: attributes.isPlaced,
          submitted: attributes.submitted,
        },
      },
      onCompleted: attributes.onCompleted,
    });
  };

  const handleOnClose = () => {
    const getTrackingDialogName = () => {
      if (showDialog === 'UpgradeAdDialog') {
        return "Candidate Management - Placement - Can't Find The Right Candidate";
      }

      if (showDialog === 'AdPerformanceDialog') {
        return 'Candidate Management - Placement - Improve Job Ad';
      }

      if (showDialog === 'HiringAdviceDialog') {
        return 'Candidate Management - Placement - Improve Next Job Ad';
      }

      if (showDialog === 'UpdateErrorDialog') {
        return "Candidate Management - Placement - Couldn't Update Hiring Status";
      }

      if (placementV2.__typename !== 'JobPlacementV2ResponseSuccess') {
        return "Candidate Management - Placement - Can't Display Hiring Status";
      }

      return isTriggeredFromHeaderLink
        ? 'Candidate Management - Placement - Update Hiring Status'
        : 'Candidate Management - Placement - Hiring Check-In';
    };

    hideJobPlacementInLocalStorage();
    trackJobPlacementDialogDismissedEvent(getTrackingDialogName());
    onClose();
  };

  if (confettiRunning) {
    return (
      <Confetti
        height={jobSectionRect?.height}
        width={jobSectionRect?.width}
        top={jobSectionRect?.top}
        left={jobSectionRect?.left}
        isConfettiRunning={confettiRunning}
        stopConfetti={() => {
          setConfettiRunning(false);
          onClose();
        }}
      />
    );
  }

  if (placementV2.__typename !== 'JobPlacementV2ResponseSuccess') {
    return (
      <Dialog
        id="placement-display-error-dialog"
        onClose={handleOnClose}
        closeLabel={t('Close')}
        open={open}
        title={t("We can't display the hiring status")}
        illustration={<ConnectionPlugIllustration />}
      >
        <ErrorDialogContent
          open={open}
          onClose={handleOnClose}
          isFailToGetPlacementStatusError={isTriggeredFromHeaderLink}
          trackErrorDialogDisplayed={() =>
            trackJobPlacementDialogDisplayedEvent(
              "Candidate Management - Placement - Can't Display Hiring Status",
            )
          }
        />
      </Dialog>
    );
  }

  if (showDialog === 'UpgradeAdDialog') {
    const titleId = 'placement-update-ad-dialog';

    return (
      <ImageModal
        isOpen={open}
        close={handleOnClose}
        image={<FreemiumImage />}
        titleId={titleId}
      >
        <UpgradeAdDialogContent
          link={router.upgradeJob({
            jobId,
            ref: 'placement-survey-candidate-management',
          })}
          titleId={titleId}
          trackButtonPressed={() =>
            trackJobPlacementUpgradeButtonPressedEvent()
          }
          trackModalDisplayed={() =>
            trackJobPlacementDialogDisplayedEvent(
              "Candidate Management - Placement - Can't Find The Right Candidate",
            )
          }
        />
      </ImageModal>
    );
  }

  if (showDialog === 'AdPerformanceDialog') {
    return (
      <Dialog
        id="placement-ad-performance-dialog"
        onClose={handleOnClose}
        closeLabel={t('Close')}
        open={open}
        title={t("Let's help you improve your job ad")}
        illustration={<DataPresentationIllustration />}
      >
        <AdPerformanceDialogContent
          link={router.adPerformance({
            jobId,
            ref: 'candiman button',
          })}
          onViewReportButtonClicked={() =>
            trackJobPlacementViewReportButtonPressedEvent()
          }
          trackAdPerformanceDialogDisplayed={() =>
            trackJobPlacementDialogDisplayedEvent(
              'Candidate Management - Placement - Improve Job Ad',
            )
          }
        />
      </Dialog>
    );
  }

  if (showDialog === 'HiringAdviceDialog') {
    return (
      <Dialog
        id="placement-hiring-advice-dialog"
        onClose={handleOnClose}
        closeLabel={t('Close')}
        open={open}
        title={t("Let's help you improve your next job ad")}
        illustration={<CoffeeAndBookIllustration />}
      >
        <HiringAdviceDialogContent
          link={router.hiringAdvice()}
          onViewMoreButtonClicked={() =>
            trackJobPlacementViewHiringAdviceButtonPressedEvent()
          }
          trackHiringAdviceDialogDisplayedEvent={() =>
            trackJobPlacementDialogDisplayedEvent(
              'Candidate Management - Placement - Improve Next Job Ad',
            )
          }
          trackJobPlacementHiringAdviceLinkPressedEvent={(title) =>
            trackJobPlacementHiringAdviceLinkPressedEvent(title)
          }
        />
      </Dialog>
    );
  }

  if (showDialog === 'UpdateErrorDialog') {
    return (
      <Dialog
        id="placement-update-error-dialog"
        onClose={handleOnClose}
        closeLabel={t('Close')}
        open={open}
        title={t("We couldn't update the hiring status")}
        illustration={<ConnectionPlugIllustration />}
      >
        <ErrorDialogContent
          open={open}
          onClose={handleOnClose}
          trackErrorDialogDisplayed={() =>
            trackJobPlacementDialogDisplayedEvent(
              "Candidate Management - Placement - Couldn't Update Hiring Status",
            )
          }
        />
      </Dialog>
    );
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!currentJobPlacement) {
      setShouldShowErrorMessage(true);
      return;
    }

    const showNextDialog = () => {
      if (shouldShowAdPerformanceDialog) {
        setShowDialog('AdPerformanceDialog');
      }

      if (shouldShowUpgradeAdDialog) {
        setShowDialog('UpgradeAdDialog');
      }

      if (!shouldShowUpgradeAdDialog && !shouldShowAdPerformanceDialog) {
        setShowDialog('HiringAdviceDialog');
      }
    };

    if (currentJobPlacement === 'yes') {
      handleUpdateJobPlacement({
        submitted: true,
        isPlaced: true,
        onCompleted: () => {
          setConfettiRunning(true);
          onPlacementStatusChange('FILLED');
        },
      });
      trackJobPlacementDataSubmissionEvent('Yes');
    }

    if (currentJobPlacement === 'no') {
      handleUpdateJobPlacement({
        submitted: true,
        isPlaced: false,
        onCompleted: () => {
          onPlacementStatusChange('NOT_FILLED');
          showNextDialog();
        },
      });
      trackJobPlacementDataSubmissionEvent('No');
    }

    if (currentJobPlacement === 'not yet') {
      handleUpdateJobPlacement({
        submitted: true,
        isPlaced: undefined,
        onCompleted: () => {
          onPlacementStatusChange('IN_PROGRESS');
          showNextDialog();
        },
      });
      trackJobPlacementDataSubmissionEvent('Not yet');
    }
  };

  return (
    <Dialog
      id="job-placement-dialog"
      open={open}
      title={t(
        isTriggeredFromHeaderLink ? 'Update hiring status' : 'Hiring check-in',
      )}
      onClose={() => {
        if (!isTriggeredFromHeaderLink) {
          handleUpdateJobPlacement({
            submitted: false,
            isPlaced: undefined,
            onCompleted: undefined,
          });
        }
        handleOnClose();
      }}
      closeLabel={t('Close')}
    >
      <TrackOnMount
        onMount={() =>
          trackJobPlacementDialogDisplayedEvent(
            isTriggeredFromHeaderLink
              ? 'Candidate Management - Placement - Update Hiring Status'
              : 'Candidate Management - Placement - Hiring Check-In',
          )
        }
      >
        <form onSubmit={handleSubmit}>
          <Stack space="large">
            <Stack space="small">
              <Text weight="strong" id="job-placement-dialog-title">
                {t('Were you able to fill this role using SEEK?')}
              </Text>
              <Inline space="medium">
                <RadioGroup
                  id="job-placement-dialog-radio-group"
                  value={currentJobPlacement}
                  onChange={({ currentTarget: { value } }) => {
                    setCurrentJobPlacement(value as JobPlacementFormValue);
                    setShouldShowErrorMessage(false);
                  }}
                  tone={shouldShowErrorMessage ? 'critical' : 'neutral'}
                  aria-labelledby="job-placement-dialog-title"
                  message={
                    shouldShowErrorMessage &&
                    t('Please select an option to update the hiring status')
                  }
                  disabled={jobPlacementMutationLoading}
                >
                  <RadioItem
                    data={{ testid: 'job-placement-radio-input-yes' }}
                    label={t('Yes')}
                    value="yes"
                  />
                  <RadioItem
                    data={{ testid: 'job-placement-radio-input-no' }}
                    label={t('No')}
                    value="no"
                  />
                  <RadioItem
                    data={{ testid: 'job-placement-radio-input-not-yet' }}
                    label={getLabelAndTrackingBasedOnVariant().label}
                    value="not yet"
                  />
                </RadioGroup>
              </Inline>
            </Stack>
            <Inline space="medium">
              <Button
                onClick={() => {
                  if (!isTriggeredFromHeaderLink) {
                    handleUpdateJobPlacement({
                      submitted: false,
                      isPlaced: undefined,
                      onCompleted: undefined,
                    });
                  }
                  handleOnClose();
                }}
                variant="soft"
              >
                {t('Cancel')}
              </Button>
              <Button
                type="submit"
                tone="brandAccent"
                variant="solid"
                loading={jobPlacementMutationLoading}
              >
                {t('Submit')}
              </Button>
            </Inline>
          </Stack>
        </form>
      </TrackOnMount>
    </Dialog>
  );
};
